import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Paper, InputLabel, IconButton, CircularProgress, Box, Snackbar, Alert } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { styled } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';

const filter = createFilterOptions();

// Project font stack (defined in assets/css/fontface.css and _css/fonfac.css)
const PROJECT_FONT = '"Customfont", "Prompt", "mainfont", sans-serif';

const StyledAutocomplete = styled(Autocomplete)({
  "& .MuiInputLabel-outlined:not(.MuiInputLabel-shrink)": {
    transform: "translate(34px, 20px) scale(1);"
  },
  "& .MuiAutocomplete-inputRoot": {
    color: "black",
    fontFamily: PROJECT_FONT,
    '&[class*="MuiOutlinedInput-root"] .MuiAutocomplete-input:first-child': {
      paddingLeft: 26
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "green"
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "red"
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "purple"
    }
  }
});

/**
 * ProductSearch Component - Composition pattern wrapper for UseAutocompletes
 * Provides search functionality with manual trigger (button or Enter key)
 * 
 * @param {Function} onSearch - Async function to fetch data: (searchText) => Promise<Array>
 * @param {Function} onSelect - Callback when item is selected: (selectedItem) => void
 * @param {String} title - Label text for the input
 * @param {String} placeholder - Placeholder text
 * @param {Number} minSearchLength - Minimum characters before search is allowed (default: 2)
 */
export default function ProductSearch({ 
  onSearch, 
  onSelect, 
  title = "สินค้า",
  placeholder = "พิมพ์รหัส/ชื่อสินค้า แล้วกด Enter หรือปุ่มค้นหา",
  minSearchLength = 1
}) {
  const [searchText, setSearchText] = useState('');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [value, setValue] = useState(null);
  const [error, setError] = useState('');
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState('');
  const [snackSeverity, setSnackSeverity] = useState('info');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  // Clear error message after 3 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const handleSearch = useCallback(async () => {
    // ใช้ค่าที่อยู่ใน input (searchText) ในการค้นหา
    const trimmedText = searchText.trim();
    
    console.log('🔍 ProductSearch - Searching for:', trimmedText);
    
    // Validation
    if (trimmedText.length < minSearchLength) {
      setError(`กรุณาพิมพ์อย่างน้อย ${minSearchLength} ตัวอักษร`);
      setData([]);
      return;
    }

    setError('');
    setLoading(true);
    
    try {
      const result = await onSearch(trimmedText);
      console.log('🔍 ProductSearch - Raw result:', result);
      console.log('🔍 ProductSearch - Result length:', result?.length);
      console.log('🔍 ProductSearch - First item fields:', result?.[0] ? Object.keys(result[0]) : 'No data');
      
      setData(Array.isArray(result) ? result : []);
      
      if (!result || result.length === 0) {
        setError('ไม่พบข้อมูล');
        setSnackMsg('ไม่พบข้อมูล');
        setSnackSeverity('info');
        setSnackOpen(true);
        setDropdownOpen(false); // Hide dropdown when no data
      } else {
        console.log('✅ ProductSearch - Found', result.length, 'items, opening dropdown');
        setDropdownOpen(true); // Show dropdown when data exists
      }
    } catch (err) {
      console.error('ProductSearch error:', err);
      setError('เกิดข้อผิดพลาดในการค้นหา');
      setSnackMsg('เกิดข้อผิดพลาดในการค้นหา');
      setSnackSeverity('error');
      setSnackOpen(true);
      setData([]);
      setDropdownOpen(false); // Hide dropdown on error
    } finally {
      setLoading(false);
    }
  }, [searchText, onSearch, minSearchLength]);

  const handleSnackClose = useCallback((event, reason) => {
    if (reason === 'clickaway') return;
    setSnackOpen(false);
  }, []);

  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  }, [handleSearch]);

  const handleChange = useCallback((event, newValue) => {
    console.log('🎯 ProductSearch - Selection changed:', newValue);
    
    if (newValue) {
      setValue(newValue);
      // Return value in the same format as UseAutocompletes
      // Support multiple field names: NAMETH (from CSPRODUCT), MYNAMETH (standard), NAMESEARCH
      const displayName = newValue.NAMETH || newValue.MYNAMETH || newValue.NAMESEARCH || newValue.CODE;
      console.log('🎯 ProductSearch - Display name:', displayName, '| CODE:', newValue.CODE);
      
      // Set searchText to display name when selected from list
      setSearchText(displayName);
      onSelect([{ CODE: newValue.CODE, MYNAMETH: displayName }]);
    } else {
      setValue(null);
      setSearchText('');
      onSelect([{ CODE: '', MYNAMETH: '' }]);
    }
  }, [onSelect]);

  const handleInputChange = useCallback((event, newInputValue, reason) => {
    // Only allow input change when typing for search, not when selecting
    if (reason === 'input') {
      setSearchText(newInputValue);
      
      // Clear selection if input is cleared
      if (newInputValue === '') {
        setValue(null);
        setData([]);
        setError('');
        setDropdownOpen(false);
        onSelect([{ CODE: '', MYNAMETH: '' }]);
      }
    }
  }, [onSelect]);

  const handleFocus = useCallback(() => {
    setIsFocused(true);
    // แสดงรหัสสินค้าเมื่อ focus
    if (value && value.CODE) {
      console.log('👁️ ProductSearch - Focus, showing CODE:', value.CODE);
      setSearchText(value.CODE);
    }
  }, [value]);

  const handleBlur = useCallback(() => {
    setIsFocused(false);
    // แสดงชื่อสินค้าเมื่อ blur
    if (value) {
      const displayName = value.NAMETH || value.MYNAMETH || value.NAMESEARCH || value.CODE;
      console.log('👁️ ProductSearch - Blur, showing name:', displayName);
      setSearchText(displayName);
    }
  }, [value]);

  return (
    <Paper
      elevation={0}
      component="div"
      sx={{
        pl: '5px',
        pt: '0px',
        m: "0px",
        display: 'flex',
        alignItems: 'center',
        backgroundColor: "#eee",
        border: "1px solid #ccc",
        verticalAlign: "middle",
      }}
    >
      <InputLabel 
        sx={{
          width: 'auto',
          display: 'inline-block',
          whiteSpace: 'nowrap',
          fontSize: '14px',
          fontFamily: PROJECT_FONT,
          color: 'black',
          verticalAlign: 'middle',
          minWidth: '50px', 
                    color: '#555',
                    verticalAlign: 'middle',
                    
                    fontWeight: '600',
          mr: 1
        }}
      >
        {title}
      </InputLabel>
      
      <Box sx={{ position: 'relative', flex: 1 }}>
        <StyledAutocomplete
          fullWidth
          open={dropdownOpen}
          onOpen={() => {
            if (data.length > 0) {
              setDropdownOpen(true);
            }
          }}
          onClose={() => setDropdownOpen(false)}
          value={value}
          onChange={handleChange}
          onInputChange={handleInputChange}
          inputValue={searchText}
          options={data}
          loading={loading}
          loadingText="กำลังค้นหา..."
          noOptionsText={error || "พิมพ์แล้วกด Enter หรือ ปุ่มค้นหา"}
          filterOptions={(options) => options}
          getOptionLabel={(option) => {
            if (typeof option === 'string') return option;
            // Support multiple field names - NAMETH first (from CSPRODUCT API)
            const label = option.NAMETH || option.MYNAMETH || option.NAMESEARCH || option.CODE || '';
            console.log('🏷️ ProductSearch - getOptionLabel:', label, 'for CODE:', option.CODE);
            return label;
          }}
          renderOption={(props, option) => {
            // Support multiple field names for display - NAMETH first (from CSPRODUCT API)
            const displayName = option.NAMETH || option.MYNAMETH || option.NAMESEARCH || '';
            return (
              <li {...props} style={{ fontFamily: PROJECT_FONT }}>
                <Box>
                  <Box sx={{ fontWeight: 'bold', fontSize: '14px' }}>
                    {option.CODE}
                  </Box>
                  <Box sx={{ fontSize: '12px', color: '#666', fontFamily: PROJECT_FONT }}>
                    {displayName}
                  </Box>
                </Box>
              </li>
            );
          }}
          renderInput={(params) => (
            <TextField 
              {...params}
              variant="standard"
              size="small"
              placeholder={placeholder}
              onKeyPress={handleKeyPress}
              onFocus={handleFocus}
              onBlur={handleBlur}
              sx={{ 
                backgroundColor: "#fff",
                "& .MuiOutlinedInput-root .MuiAutocomplete-input": {
                  height: "0.4em",
                  backgroundColor: "#fff",
                  marginTop: "-8px",
                  padding: "7.5px 4px 7.5px 6px",
                  fontFamily: PROJECT_FONT,
                  fontSize: "14px"
                }
              }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? (
                      <CircularProgress 
                        size={20} 
                        sx={{ mr: 1 }} 
                      />
                    ) : (
                      <IconButton
                        onClick={handleSearch}
                        disabled={loading || searchText.trim().length < minSearchLength}
                        size="small"
                        sx={{ mr: 1 }}
                        title={`ค้นหา (อย่างน้อย ${minSearchLength} ตัวอักษร)`}
                      >
                        <SearchIcon />
                      </IconButton>
                    )}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          sx={{
            "& label.Mui-focused": {
              display: "none",
            },
            "& legend": {
              display: "none",
            },
            "& .MuiButtonBase-root .MuiIconButton-root": {
              marginTop: "-8px"
            }
          }}
        />
        
        {error && (
          <Box 
              sx={{ 
              fontSize: '12px', 
              color: 'error.main', 
              mt: 0.5,
              ml: 1,
              fontFamily: PROJECT_FONT
            }}
          >
            {error}
          </Box>
        )}
      </Box>
      <Snackbar
        open={snackOpen}
        autoHideDuration={4000}
        onClose={handleSnackClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={handleSnackClose} severity={snackSeverity} sx={{ width: '100%' }}>
          {snackMsg}
        </Alert>
      </Snackbar>
    </Paper>
  );
}

ProductSearch.propTypes = {
  onSearch: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  title: PropTypes.string,
  placeholder: PropTypes.string,
  minSearchLength: PropTypes.number,
};
